import { DatedDelta } from './transitionDeltaSeries';

export type AverageType = 'simple' | 'exponential' | 'weighted' | 'hull';
export type AverageAlignment = 'trailing' | 'centered';

export interface AverageOptions {
  type: AverageType;
  window: number;
  alignment: AverageAlignment;
  minimumMeasuredDays?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const calendarDay = (date: string): number => {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  return Date.UTC(year, month - 1, day) / DAY_MS;
};

const windowBounds = (window: number, alignment: AverageAlignment): [number, number] =>
  alignment === 'trailing'
    ? [-(window - 1), 0]
    : [-Math.floor((window - 1) / 2), Math.ceil((window - 1) / 2)];

const weightedMean = (
  day: number,
  values: Map<number, number>,
  window: number,
  alignment: AverageAlignment,
  type: Exclude<AverageType, 'hull'>,
): { value: number | null; count: number } => {
  const [start, end] = windowBounds(window, alignment);
  const alpha = 2 / (window + 1);
  let total = 0;
  let weightTotal = 0;
  let count = 0;
  for (let offset = start; offset <= end; offset += 1) {
    const value = values.get(day + offset);
    if (value === undefined) continue;
    const age = Math.abs(offset);
    const weight = type === 'simple' ? 1
      : type === 'exponential' ? (1 - alpha) ** age
      : alignment === 'trailing' ? window - age
      : Math.max(1, Math.floor((window - 1) / 2) + 1 - age);
    total += value * weight;
    weightTotal += weight;
    count += 1;
  }
  return { value: count ? total / weightTotal : null, count };
};

/** Windows span whole calendar days: trailing d-w+1..d or centered around d.
 * Only measured days contribute, with default minimum ceil(w/2). Simple uses
 * equal weights; weighted uses linear distance; exponential uses (1-2/(w+1))
 * to the distance power. Hull applies WMA_s to 2*WMA_floor(w/2)-WMA_w on
 * measured days, with s=floor(sqrt(w)); all weights normalize over days present. */
export const movingAverage = (
  points: DatedDelta[], options: AverageOptions, plotDates: string[] = points.map(({ date }) => date),
): (number | null)[] => {
  const values = new Map(points.flatMap(({ date, value }) =>
    value === null ? [] : [[calendarDay(date), value] as const]));
  const { window, alignment, type } = options;
  const minimumMeasuredDays = options.minimumMeasuredDays ?? Math.ceil(window / 2);
  const firstDay = points.length ? calendarDay(points[0].date) : Infinity;
  const lastDay = points.length ? calendarDay(points[points.length - 1].date) : -Infinity;
  const hullValues = new Map<number, number>();
  if (type === 'hull') {
    values.forEach((_value, day) => {
      const short = weightedMean(day, values, Math.floor(window / 2), alignment, 'weighted').value;
      const long = weightedMean(day, values, window, alignment, 'weighted').value;
      if (short !== null && long !== null) hullValues.set(day, 2 * short - long);
    });
  }
  return plotDates.map((date) => {
    const day = calendarDay(date);
    if (day < firstDay || day > lastDay) return null;
    const originalCount = weightedMean(day, values, window, alignment, 'simple').count;
    if (originalCount < minimumMeasuredDays) return null;
    return type === 'hull'
      ? weightedMean(day, hullValues, Math.max(1, Math.floor(Math.sqrt(window))), alignment, 'weighted').value
      : weightedMean(day, values, window, alignment, type).value;
  });
};

export interface TrendFit {
  slope: number;
  values: (number | null)[];
}

export const fitThirtyDayTrend = (
  points: DatedDelta[],
  plotDates: string[] = points.map(({ date }) => date),
  rangeEnd: string | undefined = points[points.length - 1]?.date,
): TrendFit | null => {
  if (points.length === 0) return null;
  const end = calendarDay(rangeEnd ?? points[points.length - 1].date);
  const start = end - 29;
  const byDay = new Map<number, number>();
  points.forEach(({ date, value }) => {
    const day = calendarDay(date);
    if (value !== null && day >= start && day <= end) byDay.set(day, value);
  });
  const measured = Array.from(byDay, ([day, value]) => ({ x: day - end, y: value }));
  if (measured.length < 5) return null;
  const meanX = measured.reduce((sum, point) => sum + point.x, 0) / measured.length;
  const meanY = measured.reduce((sum, point) => sum + point.y, 0) / measured.length;
  const numerator = measured.reduce((sum, point) => sum + (point.x - meanX) * (point.y - meanY), 0);
  const denominator = measured.reduce((sum, point) => sum + (point.x - meanX) ** 2, 0);
  if (denominator === 0) return null;
  const slope = numerator / denominator;
  return {
    slope,
    values: plotDates.map((date) => {
      const day = calendarDay(date);
      return day >= start && day <= end ? meanY + slope * (day - end - meanX) : null;
    }),
  };
};
